import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";

import dayjs, { Dayjs } from "dayjs";
import { addDays, isBefore, startOfDay, setHours, setMinutes, setSeconds } from 'date-fns';
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'
import { DayButton, ScheduleContainer } from "./Styles";
import { DatePicker, Radio, Switch, TimePicker, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { SeriesSchedule } from "@/types/scheduler.d";

dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(localeData)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

interface SetScheduleProps {
    isEditing: boolean;
    notify: {
        warning: (msg: string) => void;
        error: (msg: string) => void;
    };
    schedule: string;
    setSchedule: Dispatch<SetStateAction<string>>;
    selectedStartDate: Date;
    setSelectedStartDate: Dispatch<SetStateAction<Date>>;
    selectedEndDate: Date | null;
    setSelectedEndDate: Dispatch<SetStateAction<Date | null>>;
    startTime: Date | null;
    setStartTime: Dispatch<SetStateAction<Date | null>>;
    endTime: Date | null;
    setEndTime: Dispatch<SetStateAction<Date | null>>;
    timeOption: string;
    setTimeOption: Dispatch<SetStateAction<string>>;
    endDateOption: string;
    setEndDateOption: Dispatch<SetStateAction<string>>;
    isPaused?: boolean,
    setIsPaused?: Dispatch<SetStateAction<boolean>>,
    isScheduleFound?: boolean,
    isScheduleRecurring?: boolean,
    prevStartTime?: Dayjs | null
}

const SetSchedule: React.FC<SetScheduleProps> = ({
    isEditing,
    notify,
    schedule,
    setSchedule,
    selectedStartDate, // starttime
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    timeOption,
    setTimeOption,
    endDateOption,
    setEndDateOption,
    isPaused = false,
    setIsPaused = () => { },
    isScheduleFound = true,
    isScheduleRecurring = true,
    prevStartTime=null
}) => {
    const { t } = useTranslation("common");

    const [dayOfWeek, setDayOfWeek] = useState<string>(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date().getDay()]);
    const [selectedStartTime, setSelectedStartTime] = useState<Dayjs | null>(dayjs()); //dayjs only used in this file

    //edit 모달 통해 들어올경우, start time 을 따로 설정해준다
    useEffect(()=>{
        setSelectedStartTime(prevStartTime);
    }, [prevStartTime]);

    useEffect(() => {
        let now = new Date();
        let selectedDate = selectedStartTime ? selectedStartTime.toDate() : now;

        let newSelectedDate = selectedDate;

        if (schedule === SeriesSchedule.WEEKLY) {
            const currentDay = selectedDate.getDay();
            const targetDay = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(dayOfWeek);
            let daysToAdd = (targetDay + 7 - currentDay) % 7;
            if (daysToAdd === 0 && isBefore(selectedDate, now)) {
                daysToAdd = 7;
            }
            newSelectedDate = addDays(selectedDate, daysToAdd);
            if(addDays(now, 7) < newSelectedDate) newSelectedDate = addDays(newSelectedDate, -7);
            if(newSelectedDate < addDays(now, -7)) newSelectedDate = addDays(newSelectedDate, 7);
        }
        
        setSelectedStartDate(newSelectedDate);
    }, [schedule, selectedStartTime, dayOfWeek]);


    const onEndDateChange = useCallback((date: Dayjs | null) => {
        if (date) {
             //end 시점은 DatePicker 로만 설정하므로 무조건 선택된 day 의 끝시점으로 설정한다
            const endOfDay = setHours(setMinutes(setSeconds(date.toDate(), 59), 59), 23);
            if (isBefore(endOfDay, selectedStartDate)) {
                notify.warning('End time must be after start time');
                return;
            }
            setSelectedEndDate(endOfDay);
        } else {
            setSelectedEndDate(date);
        }
    }, [notify, selectedStartDate, setSelectedEndDate]);

    const onDayButtonClick = useCallback((day: string) => {
        setDayOfWeek(day);
    },[setDayOfWeek]);

    return (
        <ScheduleContainer isEditing={isEditing}>
            {(isEditing) ? (
                <>
                    <div className="titleRow">
                        {t('Edit Schedule')}
                    </div>
                    <div className="scheduleRow">
                        <div className='labelWrapper'>
                            <label>{t("Frequency")}<Tooltip title={t("Choose how often you want the videos to be created")}> <QuestionCircleOutlined style={{ fontSize: 'x-small' }} /></Tooltip></label>
                        </div>
                        <div className='valueWrapper'>
                            <Radio.Group value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                                <Radio value={SeriesSchedule.NONE} disabled={!isScheduleFound || !isScheduleRecurring}>{t("None")}</Radio>
                                <Radio value={SeriesSchedule.DAILY} disabled={!isScheduleFound || !isScheduleRecurring}>{t("Daily")}</Radio>
                                <Radio value={SeriesSchedule.WEEKLY} disabled={!isScheduleFound || !isScheduleRecurring}>{t("Weekly")}</Radio>
                            </Radio.Group>
                        </div>
                    </div>
                </>
            ) : (

                <>
                    <div className="titleRow">
                        {t('Schedule')}
                    </div>
                    <div className="scheduleRow">
                        <div className='labelWrapper'>
                            <label>{t("Frequency")}<Tooltip title={t("Choose how often you want the videos to be created")}> <QuestionCircleOutlined style={{ fontSize: 'x-small' }} /></Tooltip></label>
                        </div>
                        <div className='valueWrapper'>
                            <Radio.Group value={schedule} onChange={(e) => setSchedule(e.target.value)}>
                                <Radio value={SeriesSchedule.INSTANT}>{t("One-Time")}</Radio>
                                <Radio value={SeriesSchedule.DAILY}>{t("Daily")}</Radio>
                                <Radio value={SeriesSchedule.WEEKLY}>{t("Weekly")}</Radio>
                            </Radio.Group>
                        </div>
                    </div>
                </>
            )}
            {(isScheduleFound && isScheduleRecurring) ? (
                <>
                    {(schedule === SeriesSchedule.WEEKLY) && (
                        <div className="setWeekdayRow">
                            <div className='labelWrapper'>
                                <label>{t("Day of Week")}</label>
                            </div>
                            <div className='valueWrapper'>
                                <div className="dayButtonWrapper">
                                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                        <DayButton
                                            key={day}
                                            selected={day === dayOfWeek}
                                            onClick={() => onDayButtonClick(day)}
                                        >
                                            {day}
                                        </DayButton>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                    {(schedule !== SeriesSchedule.INSTANT && schedule !== SeriesSchedule.NONE) && (
                        <div className="setTimeRow">
                            <div className='labelWrapper'>
                                <label>{t("Creation Time")}<Tooltip title={t("Set the time when the video creation should start.")}> <QuestionCircleOutlined style={{ fontSize: 'x-small' }} /></Tooltip></label>
                            </div>
                            <div className='valueWrapper'>
                                <Radio.Group value={timeOption} onChange={(e) => setTimeOption(e.target.value)}>
                                    <Radio value="now">{t("Now")}</Radio>
                                    <Radio value="custom" style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{flex:"1"}}>{t("Starts at")}</span>
                                        <TimePicker
                                            style={{flex:"3"}}
                                            value={dayjs(selectedStartDate)}
                                            format="hh:mm"
                                            showNow={false}
                                            onChange={(value) => {
                                                setSelectedStartTime(value || dayjs(new Date()))
                                            }}
                                            disabled={timeOption !== 'custom'}
                                        />
                                    </Radio>
                                </Radio.Group>
                            </div>
                        </div>)}
                    {(schedule === SeriesSchedule.DAILY|| schedule === SeriesSchedule.WEEKLY) && (
                        <div className="setEndDateRow">
                            <div className='labelWrapper'>
                                <label>{t("Creation End")} <Tooltip title={t("Choose when to end video creation: no end, or by a date.")}> <QuestionCircleOutlined style={{ fontSize: 'x-small' }} /></Tooltip></label>
                            </div>
                            <div className='valueWrapper'>
                                <Radio.Group value={endDateOption} onChange={(e) => setEndDateOption(e.target.value)}>
                                    <Radio value="none">{t("None")}</Radio>
                                    <Radio value="custom" style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{flex:"1"}}>{t("Until")}</span>
                                        <DatePicker
                                            style={{flex:"3"}}
                                            value={dayjs(selectedEndDate)}
                                            onChange={(value) => onEndDateChange(value)}
                                            disabled={endDateOption !== 'custom'}
                                        />
                                    </Radio>
                                </Radio.Group>
                            </div>
                        </div>
                    )}
                    {(schedule === SeriesSchedule.NONE) && (
                        <div className="noneRow">
                            <div className="noneScheduleGuide">
                                {t(`If you select 'None', you will no longer be able to modify the schedule. To make changes to the schedule later, please activate 'Creation Pause'.`)}
                            </div>
                        </div>
                    )}
                    {(isEditing) && (
                        <div className="isPausedRow">
                            <div className="labelWrapper">
                                {t('Creation Pause')}
                            </div>
                            <div className="valueWrapper">
                                <Switch checked={isPaused} onClick={() => setIsPaused(prevState => !prevState)} />
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="noneRow">
                    <div className="noneScheduleGuide">
                        {t("This series does not have an automated video creation schedule.")}
                    </div>
                </div>
            )}
        </ScheduleContainer>
    )
}

export default SetSchedule;